import {type PointerEvent as ReactPointerEvent, type RefObject, useCallback, useState} from 'react';

// Width the divider takes up in the flex row.
const FOOTPRINT = 9;
// Extra grab area on each side of the line, overlapping the neighbouring panes.
const SLOP = 6;

/**
 * Vertical drag handle sitting between a resizable left pane and the pane to
 * its right. The left pane's width is the distance from the split container's
 * left edge to the pointer, clamped to `[minWidth, maxWidth]`. The line
 * thickens and turns primary on hover / while dragging to signal it is
 * draggable.
 */
export const SplitDivider = ({
  containerRef,
  width,
  minWidth,
  maxWidth,
  onWidthChange,
  onDraggingChange,
  lineAlign = 'center',
}: {
  containerRef: RefObject<HTMLElement | null>;
  width: number;
  minWidth: number;
  maxWidth: number;
  onWidthChange: (width: number) => void;
  // Lets the panes drop width transitions so they track the pointer.
  onDraggingChange?: (isDragging: boolean) => void;
  // 'start' draws the line on the handle's left edge, so a full-bleed rail
  // meets it with no gap (the grab area extends into the right pane).
  lineAlign?: 'center' | 'start';
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const onPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      event.preventDefault();
      const handle = event.currentTarget;
      handle.setPointerCapture(event.pointerId);
      setIsDragging(true);
      onDraggingChange?.(true);
      const left = containerRef.current?.getBoundingClientRect().left ?? 0;

      const onPointerMove = (moveEvent: PointerEvent) => {
        const next = Math.min(maxWidth, Math.max(minWidth, moveEvent.clientX - left));
        onWidthChange(next);
      };

      const onPointerUp = () => {
        setIsDragging(false);
        onDraggingChange?.(false);
        handle.releasePointerCapture(event.pointerId);
        handle.removeEventListener('pointermove', onPointerMove);
        handle.removeEventListener('pointerup', onPointerUp);
        handle.removeEventListener('pointercancel', onPointerUp);
      };

      handle.addEventListener('pointermove', onPointerMove);
      handle.addEventListener('pointerup', onPointerUp);
      handle.addEventListener('pointercancel', onPointerUp);
    },
    [containerRef, maxWidth, minWidth, onWidthChange, onDraggingChange]
  );

  const active = isDragging || isHovered;
  const isStart = lineAlign === 'start';

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-valuenow={Math.round(width)}
      aria-valuemin={minWidth}
      aria-valuemax={maxWidth}
      onPointerDown={onPointerDown}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        // Negative margins widen the grab area past the line on both sides
        // while the layout footprint stays FOOTPRINT wide, so panes don't shift.
        width: FOOTPRINT + (isStart ? SLOP : SLOP * 2),
        marginLeft: -SLOP,
        marginRight: isStart ? 0 : -SLOP,
        position: 'relative',
        zIndex: 1,
        flexShrink: 0,
        cursor: 'col-resize',
        display: 'flex',
        justifyContent: isStart ? 'flex-start' : 'center',
        touchAction: 'none',
      }}
    >
      <span
        aria-hidden
        style={{
          width: 1,
          height: '100%',
          marginLeft: isStart ? SLOP : 0,
          backgroundColor: active
            ? 'var(--dig-color__primary__base)'
            : 'var(--dig-color__border__subtle)',
          // A 1px spread thickens the line to 3px without changing its layout.
          boxShadow: active ? '0 0 0 1px var(--dig-color__primary__base)' : undefined,
          transition: 'background-color 120ms ease, box-shadow 120ms ease',
        }}
      />
    </div>
  );
};
